import "./assets/main.css"
import "vfonts/Lato.css"

import * as Sentry from "@sentry/vue"

import { createApp, h } from "vue"
import { createPinia } from "pinia"
import { NConfigProvider } from "naive-ui"
import { completeAuthRedirect } from "@/lib/completeAuthRedirect"
import { provideThemePalette } from "@/composables/global/useThemePalette"
import { provideBackgroundPattern } from "@/composables/global/useBackgroundPattern"
import { registerSW } from "virtual:pwa-register"
import { setUpdateAvailable, registerUpdateCallback } from "@/composables/global/usePwaUpdate"

import App from "./App.vue"
import router from "./router"

let hasPendingUpdate = false
const updateSW = registerSW({
  immediate: true, // ping right away
  onNeedRefresh() {
    hasPendingUpdate = true
    setUpdateAvailable(true)
  },
  onOfflineReady() {},
})

registerUpdateCallback(async () => {
  await updateSW()
})

const Root = {
  setup() {
    const themeState = provideThemePalette()
    provideBackgroundPattern()
    return () =>
      h(
        NConfigProvider,
        {
          theme: themeState.theme,
          themeOverrides: themeState.themeOverrides,
        },
        { default: () => h(App) },
      )
  },
}

async function bootstrap() {
  await completeAuthRedirect()
  const app = createApp(Root)

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // SECURITY: Disable sending PII (emails, IPs) to Sentry
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 0.1, // Capture 10% of transactions (reduced from 100% to minimize overhead)
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/shelv\.love\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Logs
    enableLogs: true,
    // SECURITY: Scrub sensitive data before sending to Sentry
    beforeSend(event) {
      // Remove PII from user context
      if (event.user) {
        delete event.user.email
        delete event.user.ip_address
        delete event.user.username
      }
      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            // Remove email/password fields
            delete breadcrumb.data.email
            delete breadcrumb.data.password
            delete breadcrumb.data.token
          }
          return breadcrumb
        })
      }
      return event
    },
  })

  app.use(createPinia())
  app.use(router)
  app.mount("#app")
}

bootstrap()

setInterval(() => updateSW(), 30 * 60 * 1000)
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") updateSW()
})

// Refresh if restored from bfcache (common in iOS standalone PWAs)
// Only reload if we're in standalone mode AND there's a pending update
window.addEventListener("pageshow", (e) => {
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches
  if ((e as PageTransitionEvent).persisted && isStandalone && hasPendingUpdate) {
    location.reload()
  }
})
